import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import Toast from '../components/toast';
import { AnalysisResultToStateColor, isEmpty } from '../components/enums';
import SelectAllToolbar from '../components/select_all_toolbar';
import { GetAnalysisIcon } from '../components/ui_helpers';
import NoItemsOverlay from '../components/no_items_overlay';

// Material UI
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';


// Helper to generate a class name from a color
function colorToClassName(color) {
	if (!color) return '';
	return 'row-color-' + color.replace('#', '').toLowerCase();
}

// DataGrid getRowClassName implementation
const getRowClassName = (params) => {
	const color = AnalysisResultToStateColor(params.row.system_analysis);
	return color ? colorToClassName(color) : '';
};

export default function UserLocationManagement() {

	const { userId } = useParams();

	const [userInfo, setUserInfo] = useState({});

	const [permissionsLoading, setPermissionsLoading] = useState(true);
	const [locationsLoading, setLocationsLoading] = useState(true);

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});
	
	// Collection of Locations permissions the User to be modified has access to
	const [selfPermissions, setSelfPermissions] = useState([]);
	const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);

	// Collection of Locations information (Name, Serial, Org Info, etc.). This should only change if requesting user location access changes. This instance would require a page refresh
	const [locationsInfo, setLocationsInfo] = useState([]);
		
	// Data shown in the table
	const [tableData, setTableData] = useState([]);
	
	const [rowCount, setRowCount] = useState(0);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
	const [sortModel, setSortModel] = useState([]);
	const [filterModel, setFilterModel] = useState({ items: [] });

	// Permissions management
	const [changesMade, setChangesMade] = useState(false);
	const [locationIDsChanged, setLocationIDsChanged] = useState([]);
	
	// Track all permission fields for each locationID
	const [modifiedPermissions, setModifiedPermissions] = useState({});

	const [selectAllState, setSelectAllState] = useState(true);

	const navigate = useNavigate();
	
	// Get the current user's locations permissions info
	useEffect(() => {
		GetSelfPermissions();
	}, []);

	// Get the selected user's info
	useEffect(() => {
		GetUserInfo();
		GetSelectedUserPermissions();
	}, [userId]);

	// Fetch paginated, sorted, filtered locations info
	useEffect(() => {
		GetLocationsInfo();
	}, [paginationModel, sortModel, filterModel]);

	// Combine the permissions data and location info into table data
	useEffect(() => {
		BuildTableData();
	}, [selfPermissions, selectedUserPermissions, locationsInfo]);

	// Get the selected user's info
	function GetUserInfo() {
		axiosInstance.get(`/users/${userId}/`)
			.then(response => {
				// Check if the user is a superuser - no editing of superusers access
				if (response.data.is_superuser) {
					navigate('/403/', { replace: true });
				}

				if (isEmpty(response.data)) {
					navigate('/404/', { replace: true });
				}
				
				setUserInfo(response.data);
			})
			.catch(err => {
				console.error(err);
				navigate('/403/', { replace: true });
			});
	}

	// Get the current user's location permissions
	function GetSelfPermissions() {
		setPermissionsLoading(true);
		axiosInstance.get(`/users/locations/`)
			.then(response => {
				setSelfPermissions(response.data)
			})
			.catch(error => {
				console.error(error);
				setSelfPermissions([]);
			})
			.finally(() => {
				setPermissionsLoading(false);
			});
	}

	// Get the selected user's location permissions
	function GetSelectedUserPermissions() {
		setPermissionsLoading(true);
		axiosInstance.get(`/users/${userId}/locations/`)
			.then(response => {
				setSelectedUserPermissions(response.data)
			})
			.catch(error => {
				console.error(error);
				setSelectedUserPermissions([]);
			})
			.finally(() => {
				setPermissionsLoading(false);
			});
	}

	// Location information retrieval with server-side pagination, sorting, and filtering
	function GetLocationsInfo() {
		setLocationsLoading(true);

		// Build query params
		const params = {
			page_size: paginationModel.pageSize,
			page: paginationModel.page + 1, // API is 1-indexed
		};

		// sorting
		if (sortModel.length > 0) {
			params.sort_field = sortModel[0].field;
			params.sort_order = sortModel[0].sort;
		}
	
		// filtering
		if (filterModel.items.length > 0 && filterModel.items[0].value) {
			params.filter_field = filterModel.items[0].field;
			params.filter_value = filterModel.items[0].value;
		}

		axiosInstance.get(`/map/`, { params })
			.then(async (response) => {
				const { locations, count } = response.data;
				setLocationsInfo(locations);
				setRowCount(count);
			})
			.catch(error => {
				console.error(error);
				setLocationsInfo([]);
			})
			.finally(() => {
				setLocationsLoading(false);
			});
	}

	// Merge the current user location permissions, selected user location permissions, and location info to build the table
	function BuildTableData() {
		if (locationsInfo.length && selfPermissions.length && selectedUserPermissions.length) {
			setTableData(
				locationsInfo.map(location => {
					const selfLocPerm = selfPermissions.find(p => p.location_id === location.location_id) || {};
					const userLocPerm = selectedUserPermissions.find(p => p.location_id === location.location_id) || {};
					const modified = modifiedPermissions[location.location_id] || {};
					return {
						...location,
						...selfLocPerm,
						...userLocPerm,
						...modified,
						has_access: modified.has_access !== undefined ? modified.has_access : (userLocPerm.location_id !== undefined && userLocPerm.location_id !== null),
						change_system_settings: modified.change_system_settings !== undefined ? modified.change_system_settings : userLocPerm.change_system_settings,
						change_limits: modified.change_limits !== undefined ? modified.change_limits : userLocPerm.change_limits,
						change_email_settings: modified.change_email_settings !== undefined ? modified.change_email_settings : userLocPerm.change_email_settings,
						change_observer_settings: modified.change_observer_settings !== undefined ? modified.change_observer_settings : userLocPerm.change_observer_settings,
						view_audit_log: modified.view_audit_log !== undefined ? modified.view_audit_log : userLocPerm.view_audit_log,
						view_notes: modified.view_notes !== undefined ? modified.view_notes : userLocPerm.view_notes,
					};
				})
			);
		} else {
			setTableData([]);
		}
	}
		
	// Get user's name and/or email for the page title
	function getUserNameOrEmail() {
		if (!isEmpty(userInfo)) {
			if (userInfo.first_name && userInfo.last_name) {
				return userInfo.first_name + ' ' + userInfo.last_name + ' (' + userInfo.email + ')';
			} else if (userInfo.first_name) {
				return userInfo.first_name + ' (' + userInfo.email + ')';
			} else if (userInfo.last_name) {
				return userInfo.last_name + ' (' + userInfo.email + ')';
			} else {
				return userInfo.email;
			}
		}

		return '';
	}

	// Save changes made to user permissions
	function SaveChanges() {
		if (!changesMade || locationIDsChanged.length === 0) { return; }

		// Build the list of changed locations using modifiedPermissions
		let changedLocations = locationIDsChanged
			.map(locationId => {
				const perms = modifiedPermissions[locationId];

				if (!perms) return undefined;
				
				return {
					location_id: locationId,
					has_access: perms.has_access,
					change_system_settings: perms.change_system_settings,
					change_limits: perms.change_limits,
					change_email_settings: perms.change_email_settings,
					change_observer_settings: perms.change_observer_settings,
					view_audit_log: perms.view_audit_log,
					view_notes: perms.view_notes,
				};
			})
			.filter(Boolean);  // Remove undefined entries

		// Post the changes to the backend
		axiosInstance.post(`/users/${userId}/locations/change/`, changedLocations)
			.then(response => {
				let failedChanges = [];

				if (response.data && response.data.length > 0) {
					response.data.results.forEach(changeResult => {
						// Check if any changes failed
						if (!changeResult.success) {
							const failedLocation = locationsInfo.find(location => location.id === changeResult.location_id);
							if (failedLocation) {
								failedChanges.push({ serial: failedLocation.serial_number, reason: changeResult.error });
							}
						}
					});
				}

				// Display a toast message based on the success of permission changes
				if (failedChanges.length > 0) {
					setToast({ severity: 'error', message: `Failed to save changes for the following locations: \r\n${failedChanges.map(change => `SN${change.serial}: ${change.reason}`).join('\r\n')}`, open: true });
				} else {
					setToast({ severity: 'success', message: 'User permission changes saved successfully.', open: true });
				}
			})
			.catch(error => {
				console.error(error);
				setToast({ severity: 'error', message: 'An error occurred while saving changes. Please try again.', open: true });
			});
	
		setChangesMade(false);
		setLocationIDsChanged([]);
	
		// Update the table data
		GetSelectedUserPermissions();
	};

	// Append the location ID to the list of locations that have been updated (locationIDsChanged)
	const handleIDsAppend = (newInteger) => {
		setLocationIDsChanged(prevState => {
			if (!prevState.includes(newInteger)) {
				return [...prevState, newInteger];
			} else {
				return prevState;
			}
		});
	};

	// Handle the switch change event and update modifiedPermissions
	const handleSwitchChange = (locationId, switchName, event) => {
		setChangesMade(true);
		handleIDsAppend(locationId);

		// Change tracking for cross-page persistence
		setModifiedPermissions(prev => {
			const prevPerms = prev[locationId] || {};
			return {
				...prev,
				[locationId]: {
					...prevPerms,
					[switchName]: event.target.checked
				}
			};
		});

		// Live table state change
		setTableData(prevTableData => {
			return prevTableData.map(row => {
				if (row.location_id === locationId) {
					return { ...row, [switchName]: event.target.checked };
				} else {
					return row;
				}
			});
		});
	};

	// Handle the Select All button click
	const handleSelectAll = () => {
		setChangesMade(true);

		// Get all location IDs currently in the table
		const allLocationIds = tableData.map(row => row.location_id);

		// Update modifiedPermissions for all locations
		setModifiedPermissions(prev => {
			const updated = { ...prev };
			allLocationIds.forEach(locationId => {
				const prevPerms = prev[locationId] || {};
				updated[locationId] = {
					...prevPerms,
					has_access: selectAllState
				};
			});
			return updated;
		});

		// Track all changed IDs
		setLocationIDsChanged(prevState => {
			const newIds = allLocationIds.filter(id => !prevState.includes(id));
			return [...prevState, ...newIds];
		});

		// Update tableData for immediate UI feedback
		setTableData(prevTableData =>
			prevTableData.map(row => ({
				...row,
				has_access: selectAllState
			}))
		);

		// Toggle selectAllState for next click
		setSelectAllState(!selectAllState);
	};
	
	const handleSaveClick = () => {
		SaveChanges();
	};

	// Render a switch for a given locationID and switchName, using modifiedPermissions if present
	const renderSwitch = (locationId, switchName, checked, disabled = false) => {
		let tooltipText = locationsLoading ? "Please wait while all Locations data loads..." : "";

		if (disabled) {
			tooltipText = "You do not have permission to change this setting.";
		}

		// Use modifiedPermissions if present
		const value =
			modifiedPermissions[locationId] && modifiedPermissions[locationId][switchName] !== undefined
				? modifiedPermissions[locationId][switchName]
				: checked;

		return (
			<div style={{ width: '100%', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Tooltip title={tooltipText} placement="top">
					<Switch
						checked={value}
						onChange={(event) => handleSwitchChange(locationId, switchName, event)}
						color='primary'
						disabled={disabled || locationsLoading}
						sx={{ backgroundColor: '#ffffff77', borderRadius: '20px' }}
					/>
				</Tooltip>
			</div>
		);
	}

	// User List table columns
	// TODO: MODIFY THE FUTURE PERMISSIONS TO DISABLE IF THE SELECTED USER DOES NOT HAVE ACCESS TO THE LOCATION
	// TODO: MODIFY THE FUTURE PERMISSIONS TO DISABLE IF THE ADMIN USER DOES NOT HAVE ACCESS TO THE PERMISSION THEMSELVES
	//  (E.G. IF THE ADMIN USER CANNOT CHANGE_SYSTEM_SETTINGS FOR A LOCATION, THEN THE CHANGE_SYSTEM_SETTINGS SWITCH SHOULD BE DISABLED FOR THAT LOCATION)
	const columns = [
		{
			field: 'system_analysis',
			headerName: 'Ana',
			headerAlign: "left",
			headerClassName: 'hidden-header',
			description: 'Overall System Analysis status',
			width: 55,
			flex: 0,
			filterable: false,
			sortable: false,
			renderCell: (params) => GetAnalysisIcon(params.value)
		},
		{
			field: 'has_access',
			headerName: 'Access',
			headerAlign: "center",
			description: 'Can the User view this location?',
			width: 85,
			flex: 0,
			filterable: false,
			sortable: false,
			hideable: false,
			renderCell: (params) => renderSwitch(params.row.location_id, 'has_access', params.value),
		},
		// {
		// 	field: 'change_system_settings',
		// 	headerName: 'System Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s system settings (not including limits)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_system_settings', params.value),
		// },
		// {
		// 	field: 'change_limits',
		// 	headerName: 'Change Limits',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s limit settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_limits', params.value),
		// },
		// {
		// 	field: 'change_email_settings',
		// 	headerName: 'Email Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s email settings (for all users)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_email_settings', params.value),
		// },
		// {
		// 	field: 'change_observer_settings',
		// 	headerName: 'Observer Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s observer settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_observer_settings', params.value),
		// },
		// {
		// 	field: 'view_audit_log',
		// 	headerName: 'View Audit Log',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Audit Log?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'view_audit_log', params.value),
		// },
		// {
		// 	field: 'view_notes',
		// 	headerName: 'View Notes',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Notes column across the site?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'view_notes', params.value),
		// },
		{
			field: 'location_name',
			headerName: 'Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Location Name',
			minWidth: 250,
			flex: 1,
		},
		{
			field: 'serial_number',
			headerName: 'Serial #',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Serial Number',
			minWidth: 75,
			flex: 1,
		},
		{
			field: 'bvs_model',
			headerName: 'Model',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'BVS Controller Model',
			minWidth: 100,
			flex: 1,
		},
		{
			field: 'organization',
			headerName: 'Organization',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Organization Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'region',
			headerName: 'Region',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Region Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'facility',
			headerName: 'Facility',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Facility Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'building',
			headerName: 'Building',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Building Hierarchy',
			minWidth: 150,
			flex: 1,
		},
	];
	
	return (
		<Paper
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				Editing Location Access for {getUserNameOrEmail()}
			</Typography>
			{locationsLoading && (
				<Box sx={{ width: '100%', mb: 1 }}>
					<LinearProgress />
				</Box>
			)}
			<style>{`
				.row-color-748592 { background-color: #74859299 !important; }
				.row-color-4b9c2e { background-color: #4b9c2e99 !important; }
				.row-color-f4b235 { background-color: #f4b23599 !important; }
				.row-color-f22635 { background-color: #f2263599 !important; }
			`}</style>
			<DataGrid
				getRowId={(row) => row.location_id}
				rows={tableData}
				columns={columns}
				loading={permissionsLoading || locationsLoading}
				autoPageSize={false}
				rowCount={rowCount}
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				sortingMode="server"
				sortModel={sortModel}
				onSortModelChange={setSortModel}
				filterMode="server"
				filterModel={filterModel}
				onFilterModelChange={(newFilterModel) => {
					setFilterModel(newFilterModel);
					setPaginationModel((prev) => ({ ...prev, page: 0 }));
				}}
				density='compact'
				getRowClassName={getRowClassName}
				slots={{
					toolbar: () => <SelectAllToolbar
						onSelectAll={handleSelectAll}
						selectAllState={selectAllState}
						columnName='Has Access'
						itemType='locations'
					/>,
					noRowsOverlay: NoItemsOverlay,
				}}
				slotProps={{
					noRowsOverlay: { noItemsText: 'No Locations' },
				}}
				sx={{
					padding: 2,
				}}
			/>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
			/>
			{ changesMade && (
				<Paper style={{ padding: 10, margin: 1, border: '1px solid lightgray' }}>
					<Box display="flex" justifyContent="space-between" alignItems="right">
						<Typography variant="body1">You have unsaved changes. Would you like to save?</Typography>
						<Box>
							<Button variant="contained" color="primary" onClick={handleSaveClick}>
								Save
							</Button>
						</Box>
					</Box>
				</Paper>
			)}
		</Paper>
	);
}